import { type AxiosResponse } from "axios";

import { queryClient } from "helpers/queryClient";

import {
    type GetTaskByExternalIdResponse,
    getTaskDependenciesDependenciesByTaskExternalIdGetV2QueryKey,
    getTasksSubTasksByExternalIdGetV2QueryKey,
    type TaskContract,
    type TaskListResponse,
} from "handlers";

import { type TaskRowType } from "entity/task/list/TaskList.types";
import { TaskListQueryKeyStart } from "entity/task/utilities/TaskList.utils";

const isTaskDetailQuery = (queryKey: readonly unknown[], taskExternalId: string): boolean => {
    // Covers both internal and external queries
    return typeof queryKey[0] === "string" && queryKey[0].endsWith(`/Tasks/${taskExternalId}`);
};

export const invalidateTaskDetailQuery = (taskExternalId: string): void => {
    void queryClient.invalidateQueries({
        predicate: (query) => isTaskDetailQuery(query.queryKey, taskExternalId),
    });
};

/**
 * Patches a single field on every detail-cache entry (internal + external) for the given task.
 * Use from mutations that only return a slice of the task (e.g. tags) when the list is being
 * invalidated separately and does not need a local cache patch.
 */
export const updateTaskDetailCache = <TField extends keyof TaskContract>(
    taskExternalId: string,
    field: TField,
    value: TaskContract[TField],
): void => {
    const taskDetailQueries = queryClient.getQueryCache().findAll({
        predicate: (query) => isTaskDetailQuery(query.queryKey, taskExternalId),
    });

    taskDetailQueries.forEach((query) => {
        queryClient.setQueryData<AxiosResponse<GetTaskByExternalIdResponse>>(
            query.queryKey,
            (oldData) => {
                if (!oldData?.data.task) return oldData;
                return {
                    ...oldData,
                    data: {
                        ...oldData.data,
                        task: { ...oldData.data.task, [field]: value },
                    },
                };
            },
        );
    });
};

/**
 * Replaces the full task object in every detail-cache entry. Use from mutations that return
 * the authoritative task, when the list is being invalidated separately.
 */
export const replaceTaskInDetailCache = (
    taskExternalId: string,
    updatedTask: TaskContract,
): void => {
    const taskDetailQueries = queryClient.getQueryCache().findAll({
        predicate: (query) => isTaskDetailQuery(query.queryKey, taskExternalId),
    });

    taskDetailQueries.forEach((query) => {
        queryClient.setQueryData<AxiosResponse<GetTaskByExternalIdResponse>>(
            query.queryKey,
            (oldData) => {
                if (!oldData?.data.task) return oldData;
                return {
                    ...oldData,
                    data: { ...oldData.data, task: updatedTask },
                };
            },
        );
    });
};

/**
 * Applies `updater` to the matching task in every task-list cache entry that holds it, and to
 * that entry only. Caches without the task — and the other tasks inside a cache that has it —
 * keep their existing object references, so the grid's SET_ROWS effect never re-maps rows that
 * did not actually change.
 *
 * The predicate matches on the query-key start rather than a generated handler key because that
 * key requires the active jobIds, which would mean a lot of prop drilling inside tasks.
 */
const updateTaskInListQueries = (
    taskExternalId: string,
    updater: (task: TaskContract) => TaskContract,
): void => {
    const taskListQueries = queryClient.getQueryCache().findAll({
        predicate: (query) => String(query.queryKey[0]) === TaskListQueryKeyStart,
    });
    taskListQueries.forEach((query) => {
        queryClient.setQueryData<AxiosResponse<TaskListResponse>>(query.queryKey, (oldData) => {
            if (!oldData?.data?.tasks) return oldData;

            const taskExists = oldData.data.tasks.some(
                (task) => task.externalId === taskExternalId,
            );
            if (!taskExists) return oldData;

            return {
                ...oldData,
                data: {
                    ...oldData.data,
                    tasks: oldData.data.tasks.map((task) =>
                        task.externalId === taskExternalId ? updater(task) : task,
                    ),
                },
            };
        });
    });
};

/**
 * Patches a single field on the given task in both the list cache and every detail-cache entry.
 * Use from mutations that only return a slice of the task (e.g. tags).
 */
export const updateQueryCacheInTaskQueries = <TField extends keyof TaskContract>(
    taskExternalId: string,
    field: TField,
    value: TaskContract[TField],
): void => {
    updateTaskInListQueries(taskExternalId, (task) => ({ ...task, [field]: value }));
    updateTaskDetailCache(taskExternalId, field, value);
};

/**
 * Replaces the full task object in every task-list cache entry that contains it.
 *
 * The Tasks grid's SET_ROWS effect re-maps its rows from this list cache, so the cache is the
 * source of truth. A grid edit that updates only reducer state (duration/description/generic
 * field / assignee saves) leaves the cache holding the pre-edit value; the next cache write (e.g.
 * a custom-field save) then re-maps that stale value over the freshly edited one — the due-date
 * flicker. Call this from those saves with the authoritative response task so cache and grid
 * state stay in sync. Mirrors the detail-cache `replaceTaskInDetailCache`.
 */
export const replaceTaskInListCache = (taskExternalId: string, updatedTask: TaskContract): void => {
    updateTaskInListQueries(taskExternalId, () => updatedTask);
};

/**
 * Invalidates all task list queries, causing them to refetch.
 */
export const invalidateTaskListQueries = (): void => {
    void queryClient.invalidateQueries({
        predicate: (query) => {
            return String(query.queryKey[0]) === TaskListQueryKeyStart;
        },
    });
};

export const invalidateTaskDependencyQueries = (...taskExternalIds: string[]): void => {
    for (const taskExternalId of taskExternalIds) {
        void queryClient.invalidateQueries({
            queryKey: getTaskDependenciesDependenciesByTaskExternalIdGetV2QueryKey(taskExternalId),
        });
    }
};

/**
 * When a list-side edit dispatches updated externalIds, this checks whether any
 * of those tasks are subtasks of the currently expanded task (the one whose
 * detail panel is open) and, if so, invalidates the panel's subtasks query so
 * the panel re-fetches and reflects the change.
 *
 * The panel reads its subtasks via `useTasksSubTasksByExternalIdGetV2(parentExternalId)`,
 * keyed on the parent's externalId. List-side edits dispatch the CHILD's externalId,
 * so without this helper the panel never sees the change. See ADO #276642.
 *
 * No-ops when:
 * - No panel is open (`expandedTaskId` is undefined).
 * - No tasks were updated.
 * - The expanded parent isn't in `rowData` (e.g. filtered out).
 * - None of the updated tasks have an `ancestorId` matching the expanded task's `taskId`.
 */
export const invalidateExpandedSubtasksIfEdited = (
    expandedTaskId: string | undefined,
    updatedTaskExternalIds: readonly string[] | undefined,
    rowData: readonly TaskRowType[],
): void => {
    if (!expandedTaskId) return;
    if (!updatedTaskExternalIds || updatedTaskExternalIds.length === 0) return;

    const expandedTaskRow = rowData.find(
        (r) => !r.isNewRow && r.task.externalId === expandedTaskId,
    );
    const expandedTaskInternalId = expandedTaskRow?.task.taskId;
    if (expandedTaskInternalId == null) return;

    const anyUpdatedIsSubtaskOfExpanded = updatedTaskExternalIds.some((externalId) => {
        const row = rowData.find((r) => !r.isNewRow && r.task.externalId === externalId);
        return row?.task.ancestorId === expandedTaskInternalId;
    });

    if (anyUpdatedIsSubtaskOfExpanded) {
        void queryClient.invalidateQueries({
            queryKey: getTasksSubTasksByExternalIdGetV2QueryKey(expandedTaskId),
        });
    }
};
